import {
  GlobalConfigType,
  setApplicationStep,
  setBorrowerAllData,
  setBorrowerGroupData,
  setGlobalConfig,
  setLoanAllData,
  setLoanDetail,
  setLoanLtvMaxLimit,
  setOverviewStep,
} from 'actions'
import cloneDeep from 'clone-deep'
import { AccountType, heldAccountRoles, SettingKey, userHasAccountType } from 'config'
import { FieldOrigin, isCustomOrigin } from 'config/loan.input.visibility.type'
import { additionalMemberValidate } from 'pages/LoanApplication/AdditionalMember/logic'
import { assetInfoValidate } from 'pages/LoanApplication/AssetInformation/logics'
import { borrowerInfoValidate } from 'pages/LoanApplication/BorrowerInformation/logic'
import { BorrowerSeperator } from 'pages/LoanApplication/config'
import { creditValidate } from 'pages/LoanApplication/CreditScore/logic'
import { customInformationValidate } from 'pages/LoanApplication/CustomInformation/logic'
import { declarationsHmdaValidate } from 'pages/LoanApplication/DeclarationsHmda/logic'
import { employmentAndIncomeValidate } from 'pages/LoanApplication/Employment/logic'
import { signMldsValidate } from 'pages/LoanApplication/MLDS/logic'
import { propertyInfoValidate } from 'pages/LoanApplication/PropertyInformation/logic'
import { signAppValidate } from 'pages/LoanApplication/SignApplication/logic'
import { trackRecordValidate } from 'pages/LoanApplication/TrackRecord/logic'
import { borrowerMenuKey } from 'pages/LoanApplication/util'
import { getLockExpired, validateLoanStructureData } from 'pages/LoanStructure/Logic'
import { isBorrowerTermSheetSatisfied } from 'pages/LoanStructure/termSheetSignLogic'
import { store } from 'reducers'
import { borrowerInitialState } from 'reducers/borrower.reducer'
import { getloanDetailInitialState, LoanType } from 'reducers/loanDetail.reducer'
import { stepInitialState } from 'reducers/step.reducer'
import Api from 'services/api'
import { getAdminConfig, getLoanOverview, isVendorEnabled } from 'services/apis'
import { isIneligiblePartyCleared } from 'stories/layouts/FloaterTabs/logic'
import { formatDate, removeComma } from 'utils'
import { setBorrowerDefaultData, setLoanDefaultData } from 'utils/loan'

export interface RestrictionContext {
  structureErrors: any[]
  applicationDateComplete: boolean
  applicationCreditScorePull: boolean
  applicationSignApp: boolean
  application: Record<string, any>
  parties: Record<string, any>
  closeParties: Record<string, any>
  scopeOfWorkDone: boolean
  submitToLoanSetup: number
  templateNumber: number
  loanIntakeTaskCleared: boolean
  closingDate: string
  lockRate: any
  asIsValue: number
  afterRepairValue: number
  appraiserAsIsProperty: number
  appraiserAfterRepairProperty: number
  termSheetSignedDate?: string
  termSheetSignedPdfKey?: string
  termSheetWetSigned?: boolean
  termSheetOverride?: boolean
}

const checkRestriction = (key: string, ctx: RestrictionContext): boolean => {
  switch (key) {
    case 'loanStructureDataComplete':
      return ctx.structureErrors.length === 0
    case 'loanStructurePrice':
      return !ctx.structureErrors.includes('Interest Rate & Base Price is Required!')
    case 'scoreMatch':
      return !ctx.structureErrors.some((e: string) => e.startsWith('Credit Score does not match'))
    case 'estimateMatch':
      return !ctx.structureErrors.includes('The Fee Worksheet does not match the input estimate.')
    case 'dataComplete':
      return ctx.applicationDateComplete
    case 'creditScorePull':
      return ctx.applicationCreditScorePull
    case 'signApplication':
      return ctx.applicationSignApp
    case 'assetLiability':
      return Object.keys(ctx.application)
        .filter((k) => k.startsWith('asset'))
        .every((k) => ctx.application[k] === 1)
    case 'accountExecutiveRequired':
      return !!ctx.parties.accountExecutive
    case 'accountManagerRequired':
      return !!ctx.parties.accountManager
    case 'loanOfficerRquired':
      return !!ctx.parties.loanOfficer
    case 'loanProcessorRequired':
      return !!ctx.parties.loanProcessor
    case 'underwriterRequired':
      return !!ctx.parties.underwriter
    case 'titleCompanyRequired':
      return !!ctx.closeParties.titleCompany
    case 'hazardCompanyRequired':
      return !!ctx.closeParties.hazardInsurance
    case 'scopeOfWork':
      return ctx.scopeOfWorkDone
    case 'submitToLoanSetupRequired':
      return ctx.submitToLoanSetup === 1
    case 'taskClearedRequired':
      return ctx.loanIntakeTaskCleared
    case 'conditionTemplateRequired':
      return ctx.templateNumber !== 0
    case 'estimatedClosingDateRequired':
      return !!ctx.closingDate
    case 'loanMustbeLocked':
      return ctx.lockRate === 1
    case 'borrowerHasSignedTermSheet':
      return isBorrowerTermSheetSatisfied({
        termSheetOverride: ctx.termSheetOverride,
        termSheetWetSigned: ctx.termSheetWetSigned,
        termSheetSignedPdfKey: ctx.termSheetSignedPdfKey,
        termSheetSignedDate: ctx.termSheetSignedDate,
      })
    case 'ineligibleFlagsCleared':
    case 'ineligiblePartiesCleared':
      return isIneligiblePartyCleared()
    case 'asIsValueLessThanAsIsAppraisedValue':
      return removeComma(ctx.asIsValue) <= removeComma(ctx.appraiserAsIsProperty)
    case 'afterRepairValueLessThanAfterRepairAppraisedValue':
      return removeComma(ctx.afterRepairValue) <= removeComma(ctx.appraiserAfterRepairProperty)
    default:
      return true
  }
}

export const passesConditions = (
  conditions: any,
  user: any,
  currentLoanStatus: string,
  ctx: RestrictionContext,
): boolean => {
  if (!conditions || !Array.isArray(conditions) || conditions.length === 0) return true
  const roles = heldAccountRoles(user)
  return conditions.some(
    (c: any) =>
      roles.some((role) => c.accountTypes?.includes(role.key) || c.accountTypes?.includes(role.base)) &&
      c.loanStatuses.includes(currentLoanStatus) &&
      (c.restrictions || []).every((r: string) => checkRestriction(r, ctx)),
  )
}

const checkStatus = (data: any) => {
  let allPass = true
  Object.keys(data).map((key) => {
    if (data[key] === -1) allPass = false
  })
  return allPass
}

export const stepUpgradeWhenRestructureReleaseNoToYes = async () => {
  await store.dispatch(
    setBorrowerGroupData('borrower', {
      signedDate: '',
      agreeToSign: false,
    }),
  )
  const { step } = await store.getState()
  let overview = cloneDeep(step.overview)

  overview.application.detail.signApplication = 0
  if (overview.application.status === 1) {
    overview.application.status = 0
  }

  await store.dispatch(setOverviewStep(overview))

  let application = cloneDeep(step.application)
  application.sign = -1
  await store.dispatch(setApplicationStep(application))

  return true
}

// A slower overview for the loan you left must not overwrite the loan you opened next.
let loanLoadGeneration = 0

export const loanOverviewAndStepLogic = async (
  loanNumber: string,
  menu: string,
): Promise<{ success: boolean; message?: string; stale?: boolean }> => {
  const generation = ++loanLoadGeneration
  return new Promise(async (resolve) => {
    const dispatchIfCurrent = async (action: any) => {
      if (generation !== loanLoadGeneration) return false
      await store.dispatch(action)
      return generation === loanLoadGeneration
    }
    const resolveIfCurrent = (result: { success: boolean; message?: string }) => {
      if (generation !== loanLoadGeneration) resolve({ success: false, stale: true })
      else resolve(result)
    }
    try {
      Api.setLoanNumber(loanNumber)
      let { overview, application } = stepInitialState()

      const { globalConfigReducer } = store.getState()
      let loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields]
      let globalSettingV2 = globalConfigReducer[GlobalConfigType.GlobalSettingV2]

      if (Object.keys(loanFieldProperties).length == 0)
        loanFieldProperties = await getAdminConfig('loanFieldProperties')

      const fieldProperties = loanFieldProperties[FieldOrigin.LoanStructure] || {}

      if (loanNumber === 'new') {
        let [loanGlobalSettings, pricingEngine] = await Promise.all([
          getAdminConfig(SettingKey.GLOBAL_SETTINGS),
          isVendorEnabled('Rtltrader'),
        ])

        await dispatchIfCurrent(setOverviewStep(overview))
        await dispatchIfCurrent(setApplicationStep(application))

        // Same resolution as saveToPipeline: profile defaultChannel, else company default.
        // Wholesale is LoanType.DEFAULT (0), so use != null rather than a truthy check.
        const { auth } = store.getState()
        const profileChannel = auth?.profile?.defaultChannel
        const startingChannel =
          profileChannel != null && profileChannel !== ''
            ? Number(profileChannel)
            : loanGlobalSettings?.defaultChannel != null
            ? Number(loanGlobalSettings.defaultChannel)
            : LoanType.DEFAULT

        let loanDetailData = getloanDetailInitialState()
        loanDetailData.loanNumber = 'New'
        loanDetailData.borrowerName = '-'
        loanDetailData.propertyAddress = '-'
        loanDetailData.loanGlobalSettings = loanGlobalSettings
        loanDetailData.pricingEngine = pricingEngine
        loanDetailData.loanStatus = globalSettingV2.defaultLoanStatus
        loanDetailData.loanType = startingChannel
        // Don't wipe floater report values loaded for another navigation path.
        delete (loanDetailData as any).loanDetailsReportValues
        if (!(await dispatchIfCurrent(setLoanDetail(loanDetailData)))) return resolve({ success: false, stale: true })

        const borrowerData = borrowerInitialState()
        if (!(await dispatchIfCurrent(setBorrowerAllData(setBorrowerDefaultData(borrowerData, loanFieldProperties)))))
          return resolve({ success: false, stale: true })
        if (
          !(await dispatchIfCurrent(
            setLoanAllData(setLoanDefaultData({ type: startingChannel }, loanFieldProperties), fieldProperties),
          ))
        )
          return resolve({ success: false, stale: true })
        if (!(await dispatchIfCurrent(setLoanLtvMaxLimit({})))) return resolve({ success: false, stale: true })
        return resolveIfCurrent({ success: true })
      }

      const loanOverviewAccess = await getAdminConfig('loanOverviewAccess').catch(() => null)
      if (!(await dispatchIfCurrent(setGlobalConfig(GlobalConfigType.LoanOverviewAccess, loanOverviewAccess))))
        return resolve({ success: false, stale: true })

      // Header is this call's loan, not whatever the shared header was changed to while we waited.
      const res = await getLoanOverview(loanNumber)
      if (generation !== loanLoadGeneration) return resolve({ success: false, stale: true })

      const loanData = res.loan
      if (!(await dispatchIfCurrent(setLoanAllData(setLoanDefaultData(loanData, loanFieldProperties), fieldProperties))))
        return resolve({ success: false, stale: true })
      if (!(await dispatchIfCurrent(setLoanLtvMaxLimit({})))) return resolve({ success: false, stale: true })

      let loanDetailData = getloanDetailInitialState()

      loanDetailData.loanNumber = loanData.no
      loanDetailData.byteproFileName = loanData.byteproFileName
      loanDetailData.entityTitle = res.entityTitle
      loanDetailData.borrowingEntityName = res.borrowingEntityName || ''
      loanDetailData.borrowerName = res.borrowerNames
      loanDetailData.propertyAddress = loanData.subjectPropertyAddress
      loanDetailData.includeCoborrower = loanData.includeCoborrower
      loanDetailData.loanType = loanData.type
      loanDetailData.restructureApplicationRelease = res.process.restructureApplicationRelease
      loanDetailData.closingDate = res.process.closingDate
      loanDetailData.applicationDate = res.process.applicationDate
      loanDetailData.emailContacts = res.process.emailContacts
      loanDetailData.loanStatus = res.process.loanStatus
      loanDetailData.loanSubStatus = res.process.loanSubStatus
      loanDetailData.servicingStatus = res.process.servicingStatus
      loanDetailData.servicingPipeline = res.process.servicingPipeline
      loanDetailData.submitToAE = res.process.submitToAE
      loanDetailData.submitToLoanSetup = res.process.submitToLoanSetup
      loanDetailData.submitConditions = res.process.submitConditions
      loanDetailData.submitToUnderwriter = res.process.submitToUnderwriter
      loanDetailData.termSheetSignedDate = res.process.termSheetSignedDate || ''
      loanDetailData.termSheetSignedPdfKey = res.process.termSheetSignedPdfKey || ''
      loanDetailData.termSheetWetSigned = !!res.process.termSheetWetSigned
      loanDetailData.termSheetOverride = !!res.process.termSheetOverride
      loanDetailData.loanProcessDate = formatDate(res.process.loanProcessDate)
      loanDetailData.parties = res.parties
      loanDetailData.closeParties = res.closeParties
      loanDetailData.rateData = res.process.rateData
      loanDetailData.rush = res.process.rush
      loanDetailData.priceAdjustments = res.process.priceAdjustments
      loanDetailData.pushedToYieldeasyAt = res.process.pushedToYieldeasyAt
      // loanDetailData.crossCollateralBlanketLimit = res.assetTrack.crossCollateralLimit
      // loanDetailData.allocatedLoanAmount = res.assetTrack.allocatedLoanAmount
      loanDetailData.loanGlobalSettings = res.globalSetting
      loanDetailData.pricingEngine = res.pricingEngine
      loanDetailData.isCreatedByBorrower = res.isCreatedByBorrower
      loanDetailData.servicerLoanNumber = res.process.servicerLoanNumber
      loanDetailData.templateNumber = res.templateNumber
      loanDetailData.loanIntakeTaskCleared = res.loanIntakeTaskCleared ?? true
      loanDetailData.scopeOfWorkDone = res.scopeOfWorkDone
      loanDetailData.closingCostEstimate = res.closingCostEstimate

      let { thirdPartyId, thirdPartyData } = loanData
      if (thirdPartyData?.source == 'nexys' && thirdPartyData?.from && thirdPartyData?.byteproFileName)
        thirdPartyId = `${thirdPartyData?.from}-${thirdPartyData?.byteproFileName}`
      loanDetailData.thirdPartyId = thirdPartyId
      loanDetailData.thirdPartyData = thirdPartyData

      loanDetailData.mers = res.mers
      loanDetailData.createdAt = res.createdAt
      loanDetailData.reviewForm = res.reviewForm
      loanDetailData.escrowRepairHoldbackFunded = res.escrowRepairHoldbackFunded ?? 0

      // loanDetailData starts from getloanDetailInitialState(), which includes
      // loanDetailsReportValues: {}. Omit it so we don't wipe values already
      // fetched by FloaterTabs when entering the loan.
      delete (loanDetailData as any).loanDetailsReportValues
      if (!(await dispatchIfCurrent(setLoanDetail(loanDetailData)))) return resolve({ success: false, stale: true })

      let borrowerData = res.borrower

      Object.keys(borrowerData).forEach((borrowerSep) => {
        borrowerData[borrowerSep] = {
          ...borrowerData[borrowerSep],
          assets: [],
          liabilities: [],
          liabilitiesNotApply: false,
          tracks: [],
          rentalEstates: [],
          additionalPropertyInfo: [],
          earnestMoney: '',
          sellerCredit: '',
          otherCredit: '',
          purchaseCredits: {},
          otherAssets: { rows: [], doNotApply: false },
          expenses: { rows: [], doNotApply: false },
          additionalMembers: [],
        }

        if (!res.assetTrack[borrowerSep]) return
        ;[
          'assets',
          'liabilities',
          'liabilitiesNotApply',
          'tracks',
          'rentalEstates',
          'additionalPropertyInfo',
          'earnestMoney',
          'sellerCredit',
          'otherCredit',
          'purchaseCredits',
          'otherAssets',
          'expenses',
          'additionalMembers',
          'crossCollateralLimit',
          'allocatedLoanAmount',
        ].forEach((key) => {
          if (!res.assetTrack[borrowerSep][key]) return
          borrowerData[borrowerSep][key] = res.assetTrack[borrowerSep][key]
        })

        const filteredData = (res.assetTrack.borrower?.additionalMembers || []).filter(
          (item: any) =>
            item.borrowerSeperator === borrowerSep ||
            (borrowerSep === BorrowerSeperator.BORROWER && !item.borrowerSeperator),
        )
        if (borrowerSep === BorrowerSeperator.BORROWER) borrowerData[borrowerSep].additionalMembers = filteredData
        else
          borrowerData[borrowerSep].additionalMembers = [
            ...filteredData,
            ...borrowerData[borrowerSep].additionalMembers,
          ]
      })

      // borrowerData.borrower.assets = res.assetTrack.assets
      // borrowerData.borrower.liabilities = res.assetTrack.liabilities
      // borrowerData.borrower.tracks = res.assetTrack.tracks
      // borrowerData.borrower.rentalEstates = res.assetTrack.rentalEstates
      // // borrowerData.borrower.additionalMembers = res.assetTrack.additionalMembers
      // borrowerData.borrower.additionalPropertyInfo = res.assetTrack.additionalPropertyInfo
      // borrowerData.borrower.earnestMoney = res.assetTrack.earnestMoney
      // borrowerData.borrower.sellerCredit = res.assetTrack.sellerCredit
      // borrowerData.borrower.otherCredit = res.assetTrack.otherCredit
      // borrowerData.borrower.purchaseCredits = res.assetTrack.purchaseCredits
      // Object.keys(borrowerData).map((borrowerSeperator) => {
      //   const filteredData = res.assetTrack.additionalMembers.filter(
      //     (item: any) =>
      //       item.borrowerSeperator === borrowerSeperator ||
      //       (borrowerSeperator === BorrowerSeperator.BORROWER && !item.borrowerSeperator),
      //   )
      //   borrowerData[borrowerSeperator].additionalMembers = filteredData
      // })
      if (!(await dispatchIfCurrent(setBorrowerAllData(setBorrowerDefaultData(borrowerData, loanFieldProperties)))))
        return resolve({ success: false, stale: true })

      // Overview Step Update
      const { auth } = await store.getState()
      // Structure Step
      if (res.process.rateData.rate > 0) {
        overview.structure.detail.rateAndPrice = 1
        overview.structure.detail.lockRate = 0
      }
      if (res.process.rateData.locked) {
        overview.structure.detail.lockRate = 1
        if (getLockExpired({ rateLocked: true, expireDate: res.process.rateData.expireDate })) {
          overview.structure.detail.lockRate = 2
        }
      }
      let overview_structure_status = 1
      Object.keys(overview.structure.detail).map((key) => {
        const detail: any = overview.structure.detail
        if (detail[key] !== 1) {
          overview_structure_status = 0
        }
      })
      overview.structure.status = overview_structure_status

      const currentLoanStatus = loanDetailData.loanStatus

      const anyTimeCanSeeApplicationSubmission = userHasAccountType(auth.profile, AccountType.ADMIN)

      // Application Step

      // Each Application Steps Logic
      // - Property Info
      application.property = propertyInfoValidate(true) ? 1 : -1
      // - Borrower Info

      const validateStatus: Record<string, number> = {}
      Object.keys(borrowerData).forEach((_borrowerSeparator) => {
        const borrowerSeparator = _borrowerSeparator as any as BorrowerSeperator
        // Excluded borrowers should not block application completeness
        if (borrowerData[borrowerSeparator]?.isIncluded === false) return

        const borrowerKey = borrowerMenuKey(borrowerSeparator, 'borrower')
        validateStatus[borrowerKey] = borrowerInfoValidate(borrowerSeparator, [], {}, true) ? 1 : -1

        const employmentKey = borrowerMenuKey(borrowerSeparator, 'employment')
        validateStatus[employmentKey] = employmentAndIncomeValidate(borrowerSeparator) ? 1 : -1

        const assetKey = borrowerMenuKey(borrowerSeparator, 'asset')
        validateStatus[assetKey] = assetInfoValidate(borrowerSeparator, false) ? 1 : -1

        const trackKey = borrowerMenuKey(borrowerSeparator, 'track')
        validateStatus[trackKey] = trackRecordValidate(
          borrowerSeparator,
          borrowerData[borrowerSeparator].isTrackRecordOverride,
          borrowerData[borrowerSeparator].hasTrackRecord,
          borrowerData[borrowerSeparator].tracks,
        )
          ? 1
          : -1

        const hmdaKey = borrowerMenuKey(borrowerSeparator, 'hmda')
        validateStatus[hmdaKey] = declarationsHmdaValidate(borrowerSeparator, true, false, -1, false) ? 1 : -1

        const creditKey = borrowerMenuKey(borrowerSeparator, 'credit')
        validateStatus[creditKey] = creditValidate(borrowerSeparator, false) ? 1 : -1

        const signKey = borrowerMenuKey(borrowerSeparator, 'sign')
        validateStatus[signKey] = signAppValidate(borrowerSeparator, false) ? 1 : -1

        const additionalMemberKey = borrowerMenuKey(borrowerSeparator, 'additionalMember')
        validateStatus[additionalMemberKey] = additionalMemberValidate(borrowerSeparator) ? 1 : -1

        // Validate custom pages
        Object.keys(loanFieldProperties).forEach((fieldOrigin) => {
          if (isCustomOrigin(fieldOrigin)) {
            const customKey = borrowerMenuKey(borrowerSeparator, fieldOrigin)
            validateStatus[customKey] = customInformationValidate(borrowerSeparator, fieldOrigin, true, false) ? 1 : -1
          }
        })
      })

      application = {
        ...application,
        ...validateStatus,
      }

      application.mlds = signMldsValidate(BorrowerSeperator.BORROWER, false) ? 1 : -1
      // Application Step

      let isCorrespondentLoan = loanData.type == LoanType.CORRESPONDENT

      let applicationDateComplete =
        application.property === 1 &&
        application.employment === 1 &&
        application.borrower === 1 &&
        // application.asset === 1 &&
        application.track === 1 &&
        application.hmda === 1 &&
        application.additionalMember === 1

      // Check custom pages validation for borrower
      Object.keys(loanFieldProperties).forEach((fieldOrigin) => {
        if (isCustomOrigin(fieldOrigin)) {
          const customKey = borrowerMenuKey(BorrowerSeperator.BORROWER, fieldOrigin)
          if (application[customKey] !== 1) {
            applicationDateComplete = false
          }
        }
      })

      let applicationCreditScorePull = application.credit === 1

      let applicationSignApp = application.sign === 1

      if (loanDetailData.includeCoborrower && Object.keys(borrowerData).length > 1) {
        Object.keys(borrowerData).forEach((_borrowerSeparator) => {
          const borrowerSeparator = _borrowerSeparator as any as BorrowerSeperator
          if (borrowerSeparator === BorrowerSeperator.BORROWER) return
          if (borrowerData[borrowerSeparator]?.isIncluded === false) return

          const borrowerKey = borrowerMenuKey(borrowerSeparator, 'borrower')

          const trackKey = borrowerMenuKey(borrowerSeparator, 'track')

          const hmdaKey = borrowerMenuKey(borrowerSeparator, 'hmda')
          const creditKey = borrowerMenuKey(borrowerSeparator, 'credit')

          const signKey = borrowerMenuKey(borrowerSeparator, 'sign')

          applicationDateComplete =
            applicationDateComplete &&
            application[borrowerKey] === 1 &&
            application[hmdaKey] === 1 &&
            // application[assetKey] === 1 &&
            application[trackKey] === 1

          // Validate custom pages
          Object.keys(loanFieldProperties).forEach((fieldOrigin) => {
            if (isCustomOrigin(fieldOrigin)) {
              const customKey = borrowerMenuKey(borrowerSeparator, fieldOrigin)
              if (application[customKey] !== 1) {
                applicationDateComplete = false
              }
            }
          })
          applicationCreditScorePull = applicationCreditScorePull && application[creditKey] === 1
          applicationSignApp = applicationSignApp && application[signKey] === 1
        })
      }

      overview.application.detail.dataComplete = applicationDateComplete ? 1 : -1
      overview.application.detail.creditScorePull = applicationCreditScorePull ? 1 : -1
      overview.application.detail.signApplication = applicationSignApp ? 1 : -1
      if (isCorrespondentLoan) {
        // overview.application.status = -1
        overview.application.detail.creditScorePull = -2
        // overview.application.detail.signApplication = -2
      }

      if (
        ((loanFieldProperties as any)['visibility'] || {})['sign'] === false ||
        loanFieldProperties.properties?.sign?.visibleLoanTypes?.[loanData.type] === false
      )
        delete (overview.application.detail as any).signApplication

      if (
        ((loanFieldProperties as any)['visibility'] || {})['credit'] === false ||
        loanFieldProperties.properties?.credit?.visibleLoanTypes?.[loanData.type] === false
      )
        delete (overview.application.detail as any).creditScorePull

      const loanStructureErrors = validateLoanStructureData(loanData, loanDetailData.rateData)
      overview.structure.errors = loanStructureErrors

      if (loanStructureErrors.length === 0 || application.credit === 1 || anyTimeCanSeeApplicationSubmission) {
        overview.application.status = checkStatus(overview.application.detail) ? 1 : 0
      }

      if (!(await dispatchIfCurrent(setApplicationStep(application)))) return resolve({ success: false, stale: true })

      // Submission Step
      // loanSubmission

      overview.submit.detail.submitToAE = res.workflow.submit_loan.enabled
        ? loanDetailData.submitToAE === 1
          ? 1
          : -1
        : -2
      overview.submit.detail.submitSetup = res.workflow.submit_to_loan_setup.enabled
        ? loanDetailData.submitToLoanSetup === 1
          ? 1
          : -1
        : -2
      overview.submit.detail.conditionSubmit = res.workflow.submit_conditions.enabled
        ? loanDetailData.submitConditions === 1
          ? 1
          : -1
        : -2
      overview.submit.detail.submitUnderwriter = res.workflow.submit_to_underwriter.enabled
        ? loanDetailData.submitToUnderwriter === 1
          ? 1
          : -1
        : -2

      if (loanStructureErrors.length === 0 || anyTimeCanSeeApplicationSubmission) {
        overview.submit.status = checkStatus(overview.submit.detail) ? 1 : 0
      }

      if (!(await dispatchIfCurrent(setOverviewStep(overview)))) return resolve({ success: false, stale: true })

      const restrictionCtx: RestrictionContext = {
        structureErrors: loanStructureErrors,
        applicationDateComplete,
        applicationCreditScorePull,
        applicationSignApp,
        application,
        parties: loanDetailData.parties || {},
        closeParties: loanDetailData.closeParties || {},
        scopeOfWorkDone: !!loanDetailData.scopeOfWorkDone,
        submitToLoanSetup: loanDetailData.submitToLoanSetup,
        templateNumber: loanDetailData.templateNumber ?? 0,
        loanIntakeTaskCleared: res.loanIntakeTaskCleared ?? true,
        closingDate: loanDetailData.closingDate,
        lockRate: overview.structure.detail.lockRate,
        asIsValue: loanData.asIsValue,
        afterRepairValue: loanData.afterRepairValue,
        appraiserAsIsProperty: res.reviewForm?.appraiserAsIsProperty || 0,
        appraiserAfterRepairProperty: res.reviewForm?.appraiserAfterRepairProperty || 0,
        termSheetSignedDate: loanDetailData.termSheetSignedDate,
        termSheetSignedPdfKey: loanDetailData.termSheetSignedPdfKey,
        termSheetWetSigned: loanDetailData.termSheetWetSigned,
        termSheetOverride: loanDetailData.termSheetOverride,
      }

      if (menu === 'structure') {
        if (!passesConditions(loanOverviewAccess?.loanStructure, auth.profile, currentLoanStatus, restrictionCtx)) {
          return resolveIfCurrent({
            success: false,
            message: 'Redirect Overview',
          })
        }
      }

      if (menu === 'application') {
        if (
          overview.application.status === -1 ||
          !passesConditions(loanOverviewAccess?.loanApplication, auth.profile, currentLoanStatus, restrictionCtx)
        ) {
          return resolveIfCurrent({
            success: false,
            message: 'Redirect Overview',
          })
        }
      }

      if (menu === 'submit') {
        if (
          overview.submit.status === -1 ||
          !passesConditions(loanOverviewAccess?.loanSubmission, auth.profile, currentLoanStatus, restrictionCtx)
        ) {
          return resolveIfCurrent({
            success: false,
            message: 'Redirect Overview',
          })
        }
      }

      resolveIfCurrent({
        success: true,
      })
    } catch (error) {
      console.log('track loanOverviewAndStepLogic Error', error)
      resolveIfCurrent({
        success: false,
        message: 'Redirect Pipeline',
      })
    }
  })
}

export const loanStructureStepUpgrade = async (key: string, status: number) => {
  const { step } = await store.getState()
  let overview = cloneDeep(step.overview)
  overview.structure.detail[key] = status
  if (key === 'rateAndPrice') {
    if (status === 1) {
      overview.structure.detail.lockRate = 0
    }
    if (status === 0) {
      overview.structure.detail.lockRate = -1
    }
  }
  if (key === 'lockRate') {
    if (status === 1) {
      overview.structure.status = 1
    } else {
      overview.structure.status = 0
    }
  }
  await store.dispatch(setOverviewStep(overview))
  return true
}
